import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { systemStatus, type SystemStatus } from "@/lib/api";

const POLL_MS = 5000;

/**
 * Compact health-pill row that lives at the top of the page. Polls
 * `/api/system/status` every 5 seconds and renders four pills:
 *
 *   - Market open/closed (live derived from the server clock, not the
 *     browser — matches the tracker's poll gate exactly).
 *   - Tracker: Live when the last poll was within one interval and
 *     subscribers > 0, Idle when baselines = 0, Paused while the
 *     market is closed, or Error when lastError is set.
 *   - Scanner: Running, Stopped, Circuit Open (with countdown), or
 *     Error when lastError is set.
 *   - Last error (anywhere): inline rose text, collapsed when clean.
 *
 * Designed to be render-stable — when the fetch errors we keep the
 * previous snapshot visible and just flag the staleness in a corner,
 * so the UI doesn't flicker every 5 seconds if the server restarts.
 */
export function SystemStatusBar() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const s = await systemStatus();
        if (!cancelled) {
          setStatus(s);
          setStale(false);
        }
      } catch {
        if (!cancelled) setStale(true);
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!status) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="inline-block size-2 rounded-full bg-muted-foreground/50" />
        <span>system status…</span>
      </div>
    );
  }

  const tracker = status.tracker;
  const scanner = status.scanner;

  // Derive a tracker pill label + variant. We want the pill to tell
  // the user the exact reason the tracker isn't producing data —
  // "no baselines yet" and "market closed" feel identical at the
  // rest level but mean very different things operationally.
  const trackerPill = (() => {
    if (!tracker) {
      return { label: "tracker off", variant: "secondary" as const };
    }
    if (tracker.last_error) {
      return { label: "tracker error", variant: "destructive" as const };
    }
    if (tracker.baselines === 0) {
      return { label: "tracker idle", variant: "secondary" as const };
    }
    if (!tracker.market_open) {
      return { label: "tracker paused", variant: "secondary" as const };
    }
    // Live: we have baselines, market is open, no error. The
    // "subscribers > 0" check would let us distinguish "live but
    // nobody watching" from "live with UI attached", but the SSE
    // connection that renders this bar is itself a subscriber, so
    // once any tab is open the count is ≥ 1 trivially.
    return {
      label: `tracker live · ${tracker.baselines}`,
      variant: "default" as const,
    };
  })();

  // Scanner pill. Circuit-open state wins over running/stopped so the
  // user sees the failure mode first.
  const scannerPill = (() => {
    if (!scanner) {
      return { label: "scanner off", variant: "secondary" as const };
    }
    if (scanner.circuit_open) {
      return {
        label: `circuit open · ${scanner.circuit_cooldown_sec}s`,
        variant: "destructive" as const,
      };
    }
    if (!scanner.running) {
      return { label: "scanner stopped", variant: "secondary" as const };
    }
    const pct =
      scanner.universe_size > 0
        ? Math.min(100, (scanner.scanned / scanner.universe_size) * 100)
        : 0;
    return {
      label: `scanner ${pct.toFixed(0)}%`,
      variant: "default" as const,
    };
  })();

  const marketOpen = tracker?.market_open ?? false;

  // Merge tracker + scanner errors. Tracker error wins because it's
  // more likely to be user-visible (missing Live cells) while the
  // scanner is a background loop most users don't watch.
  const lastError = tracker?.last_error || scanner?.last_error || "";

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Badge variant={marketOpen ? "default" : "secondary"}>
        <span
          className={`mr-1 inline-block size-2 rounded-full ${
            marketOpen ? "bg-emerald-500" : "bg-amber-500"
          }`}
        />
        {marketOpen ? "market open" : "market closed"}
      </Badge>
      <Badge variant={trackerPill.variant}>{trackerPill.label}</Badge>
      <Badge variant={scannerPill.variant}>{scannerPill.label}</Badge>
      {lastError && (
        <span className="max-w-[320px] truncate font-mono text-rose-500" title={lastError}>
          {lastError}
        </span>
      )}
      {stale && (
        <span className="text-muted-foreground" title="Last status fetch failed">
          · stale
        </span>
      )}
    </div>
  );
}
